/**
 * Selection-driven bulk-action toolbar for the Ads Manager.
 *
 * Renders nothing when nothing is selected — the toolbar collapses
 * cleanly so empty placeholder buttons don't clutter the row.
 *
 * Once ≥1 row is selected:
 *   - Enable / Pause buttons (icon-only 34×34 по макету — count in title)
 *     → fire the ASA mutation against every selected id in parallel
 *   - Label dropdown (campaign / ad_group only)
 *   - Optional Actions dropdown — items branch by entityType:
 *       campaign:  Edit / Duplicate / Rename / Apply CPP
 *       ad_group:  Apply CPP / Edit bids / Edit ad group settings
 *       keyword:   Edit bids
 *
 *   The parent can intercept Actions picks via `onActionPick(value)`.
 *   Without an interceptor, picks fall through to a built-in alert
 *   stub so the menu's still discoverable.
 *   - Optional Rules dropdown (campaign grain only; UI stub)
 *
 * Selection-clear is wired through the row checkbox header — there's
 * no dedicated "Clear" pill in this toolbar.
 */

import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { LabelDropdown } from "@/components/LabelDropdown";
import { SimpleDropdown } from "@/components/SimpleDropdown";

export type BulkEntityType = "campaign" | "ad_group" | "keyword";

export function BulkActions({
  selectedIds,
  canEdit,
  onEnable,
  onPause,
  busy = false,
  entityType,
  showLabel = true,
  showActions = false,
  showRules = false,
  onToggleChart,
  chartOpen = false,
  onActionPick,
  onRulesManager,
}: {
  selectedIds: number[];
  canEdit: boolean;
  onEnable: () => void;
  onPause: () => void;
  busy?: boolean;
  entityType: BulkEntityType;
  /** Hidden for keywords (per product). */
  showLabel?: boolean;
  showActions?: boolean;
  showRules?: boolean;
  /** Кнопка «Performance over time». Открыт ли график — знает страница,
   *  поэтому нажатое состояние приходит сюда, а не выводится здесь. */
  onToggleChart?: () => void;
  chartOpen?: boolean;
  /** Optional Actions-pick interceptor. If absent, picks fall through
   *  to a stub alert. */
  onActionPick?: (value: string) => void;
  /** Opens the Rules Manager popup. Required when showRules is set. */
  onRulesManager?: () => void;
}) {
  const count = selectedIds.length;
  if (count === 0) return null;
  const disabled = !canEdit || busy;
  const noun = entityType.replace("_", " ");
  const plural = `${count} selected ${noun}${count === 1 ? "" : "s"}`;

  const actionsItems =
    entityType === "ad_group"
      ? [
          { value: "apply_cpp", label: "Apply CPP" },
          { value: "edit_bids", label: "Edit bids" },
          { value: "edit_settings", label: "Edit ad group settings" },
        ]
      : entityType === "keyword"
      ? [{ value: "edit_bids", label: "Edit bids" }]
      : [
          { value: "edit_bid_budget", label: "Edit Bid/Budget" },
          { value: "duplicate", label: "Duplicate" },
          // Rename only makes sense for a single selected campaign.
          // ASA's PUT /campaigns/{id} takes one id at a time and the
          // user can't pick one new name for many. Hide entirely when
          // multi-selected — clearer than greying out.
          ...(count === 1
            ? [{ value: "rename", label: "Rename Campaign" }]
            : []),
          { value: "apply_cpp", label: "Apply CPP" },
        ];

  const handleActionPick = (v: string) => {
    if (onActionPick) onActionPick(v);
    else window.alert(`${v} — wired in v1.1`);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Btn
        icon={I.play}
        disabled={disabled}
        onClick={onEnable}
        title={`Enable ${plural}`}
        aria-label={`Enable ${plural}`}
      />
      <Btn
        icon={I.pause}
        disabled={disabled}
        onClick={onPause}
        title={`Pause ${plural}`}
        aria-label={`Pause ${plural}`}
      />
      {showLabel && entityType !== "keyword" && (
        <LabelDropdown
          entityType={entityType}
          entityIds={selectedIds}
          disabled={disabled}
        />
      )}
      {onToggleChart && (
        // ★ Не под `canEdit`: график только читает. Смотреть динамику
        // выделенной сущности вправе и тот, кому править нельзя.
        <Btn
          icon={I.lineChart}
          aria-pressed={chartOpen}
          onClick={onToggleChart}
          title="Performance over time"
          aria-label="Performance over time"
        />
      )}
      {showActions && (
        <SimpleDropdown
          label="Actions"
          items={actionsItems}
          onPick={handleActionPick}
          disabled={disabled}
        />
      )}
      {showRules && (
        <SimpleDropdown
          label="Rules"
          items={[{ value: "rules_manager", label: "Rules Manager" }]}
          onPick={() => onRulesManager?.()}
          disabled={disabled}
        />
      )}
    </div>
  );
}
